import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { status_colors } from "../../services/shipping";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";
import { useError } from "../../contexts/ErrorContext";
import RefetchingTimestamp from "../../components/UI/RefetchingTimestamp";
import ShippingSummaryColorStatus from "./ShippingSummaryColorStatus";
import { ShippingStatusSummary_T } from "../../types/Shipping";

interface ShippingStatusSummaryTableProps {
  dateStart: string;
  dateEnd: string;
  statusSummary?: ShippingStatusSummary_T[] | null;
  isLoading?: boolean;
  showUpdateTimestamp?: boolean;
  onUpdateTimestamp?: (timestamp: number | null) => void;
}

const ShippingStatusSummaryTable: React.FC<ShippingStatusSummaryTableProps> = ({
  dateStart,
  dateEnd,
  statusSummary: propsStatusSummary,
  isLoading: propsIsLoading,
  showUpdateTimestamp = false,
  onUpdateTimestamp,
}) => {
  const { showError } = useError();

  const {
    statusSummary: localStatusSummary,
    dataUpdatedAt,
    detailsError,
    isLoading: localIsLoading,
    shippingDetailsRefetching: isRefetching,
  } = useShippingDataCoordinator(dateStart, dateEnd);

  const statusSummary = propsStatusSummary ?? localStatusSummary;
  const isLoading = propsIsLoading ?? localIsLoading;

  useEffect(() => {
    if (onUpdateTimestamp && dataUpdatedAt) {
      onUpdateTimestamp(dataUpdatedAt);
    }
  }, [dataUpdatedAt, onUpdateTimestamp]);

  useEffect(() => {
    if (detailsError) {
      showError(
        `Failed to get shipping summary for dates: ${dateStart} - ${dateEnd}`
      );
    }
  }, [detailsError, showError, dateStart, dateEnd]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <TableContainer component={Paper} sx={{ display: "block" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Color</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Count</TableCell>
              <TableCell>Percentage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statusSummary?.map(({ status, count, percentage }) => (
              <TableRow key={status}>
                <TableCell>
                  <ShippingSummaryColorStatus
                    color={status_colors[status as keyof typeof status_colors]}
                  />
                </TableCell>
                <TableCell>{status}</TableCell>
                <TableCell>{count}</TableCell>
                <TableCell>{percentage}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showUpdateTimestamp && dataUpdatedAt && (
        <Box
          sx={{ mt: 2, width: "100%", textAlign: "center", display: "block" }}
        >
          <RefetchingTimestamp
            isRefetching={isRefetching}
            updatedAtTimestamp={dataUpdatedAt}
          />
        </Box>
      )}
    </Box>
  );
};

export default ShippingStatusSummaryTable;
